import React from 'react'
import { useTheme } from "../contexts/ThemeContext";
import { useState } from 'react';
import { projectData } from '../data/Projects';

function ProjectCard({ title, description, techStack, githubUrl, demoUrl, thumbnail, videoUrl, date }) {
  const [hoverIcon, setHoverIcon] = React.useState(false);
  const { isDark } = useTheme();

  return (
    <div className={`group ${isDark ? "bg-white backdrop-blur-sm border-slate-500 shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.2)]" :
      "bg-white/80 backdrop-blur-sm border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)]"} rounded-xl overflow-hidden border transition-all duration-300`}>
      <div className="relative">
        {hoverIcon ? (
          <iframe 
            className="w-full h-[220px] object-cover border-slate-200" 
            src={`${videoUrl}?autoplay=1&mute=1&loop=1&playlist=${videoUrl.split('embed/')[1]}`}
            title={title} 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          ></iframe>
        ) : (
          <img 
            src={thumbnail} 
            alt={title} 
            className="w-full h-[220px] object-cover"
            loading="lazy"
            decoding="async"
          />
        )}
        
        <div
          className={`absolute top-3 right-3 ${ isDark ? "bg-black/20 hover:bg-white/40" : "bg-black/50 hover:bg-black" }  shadow-sm rounded-full p-2 cursor-pointer transition-all duration-300  ${hoverIcon ? 'scale-110' : 'scale-100'}`}
          onMouseEnter={() => setHoverIcon(true)}
          onMouseLeave={() => setHoverIcon(false)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5 dark:fill-white fill-white"
            viewBox="0 0 16 16"
          >
            <path d="M16 8s-3-5.333-8-5.333S0 8 0 8s3 5.333 8 5.333S16 8 16 8zm-8 3.333a3.333 3.333 0 1 1 0-6.666 3.333 3.333 0 0 1 0 6.666z" />
          </svg>
        </div>
      </div>

      <div className={`p-4 ${ isDark ? "bg-slate-700" : "bg-white/70" }`}>
        <div className="flex justify-between items-center ">
          <h3 className={`text-xl font-bold ${ isDark ? "text-gray-100" : "text-slate-800" }`}>{title}</h3>
          <span className={`text-xs ${ isDark ? "text-gray-400" : "text-slate-500" }`}>{date}</span>
        </div>
        <p className={`text-sm ${ isDark ? "text-gray-300" : "text-slate-600" } mb-3 leading-relaxed`}>
          {description}
        </p>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {techStack.split(', ').map((tech, index) => (
            <span key={index} className={`${isDark ? "bg-white/10 text-gray-200" : "bg-slate-100 text-slate-600"} px-2 py-0.5 rounded-full text-[10px] font-medium`}>
              {tech}
            </span>
          ))}
        </div>

        <div className="flex gap-4">
          <a 
            href={githubUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className={`flex items-center gap-2 px-4 py-2 ${ isDark ? "bg-white/10 text-gray-200 hover:bg-white/20" : "bg-slate-100 text-slate-700 hover:bg-slate-200" } rounded-lg text-sm font-medium  transition-colors`}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
            </svg>
            GitHub
          </a>
          <a 
            href={demoUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className={`flex items-center gap-2 px-4 py-2 ${ isDark ? "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30" : "bg-blue-50 text-blue-600 hover:bg-blue-100" } rounded-lg text-sm font-medium transition-colors`}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
              <path d="M14 2.5a.5.5 0 0 0-.5-.5h-6a.5.5 0 0 0 0 1h4.793L2.146 13.146a.5.5 0 0 0 .708.708L13 3.707V8.5a.5.5 0 0 0 1 0v-6z"/>
            </svg>
            Live Demo
          </a>
        </div>
      </div>
    </div>
  );
}

function Projects() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
      {projectData.map((project, idx) => (
        <ProjectCard key={idx} {...project} />
      ))}
    </div>
  );
}

export default Projects